import { useCallback, useEffect } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';

/** Default focusable-element selector shared by the app's modal dialogs. */
export const DEFAULT_FOCUSABLE = 'button, a[href], input, select, [tabindex]:not([tabindex="-1"])';

export interface UseFocusTrapOptions {
  /**
   * CSS selector for elements Tab should cycle through. Defaults to
   * {@link DEFAULT_FOCUSABLE}; pass a wider selector (e.g. plus
   * `[contenteditable="true"]`) for a dialog whose focusable surface includes
   * a contenteditable region (`AdvancedEditDialog`).
   */
  focusableSelector?: string;
  /**
   * When provided, focuses this element on mount and restores focus to
   * whatever had it beforehand once the dialog unmounts (`ConfirmDialog`'s
   * "land on the safe action, return focus to the opener" behaviour). Omit it
   * for a dialog that manages its own initial focus (e.g. seeding a
   * contenteditable and placing the caret).
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * Shared Tab-trap (+ optional initial-focus/restore) for the app's modal
 * dialogs (cue-forge-sdv.11.6 final-review cleanup — extracted from three
 * near-identical copies in `ConfirmDialog`, `AdvancedEditDialog`, and
 * `TranscribeDialog`). `containerRef` is the dialog panel Tab should stay
 * confined to; the returned `onKeyDown` only implements the Tab/Shift+Tab
 * wrap — Escape and any other key handling stay each caller's own
 * `onKeyDown`, so every dialog composes it however it needs (e.g.
 * Escape-to-close, `AdvancedEditDialog`'s Ctrl+B/I/U formatting shortcuts).
 */
export function useFocusTrap<T extends HTMLElement>(
  containerRef: RefObject<T | null>,
  options: UseFocusTrapOptions = {},
) {
  const { focusableSelector = DEFAULT_FOCUSABLE, initialFocusRef } = options;

  // Focus the safe/initial element on open; restore focus to the opener on
  // close. The opener may already be gone (e.g. confirming can unmount the
  // parent dialog too), so only restore while it is still connected.
  useEffect(() => {
    if (!initialFocusRef) return;
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    initialFocusRef.current?.focus();
    return () => {
      if (prev && prev.isConnected) prev.focus();
    };
    // Intentionally mount/unmount-only: re-focusing on every render would
    // fight the user's own focus changes inside the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Tab') return;
      const focusables = containerRef.current?.querySelectorAll<HTMLElement>(focusableSelector);
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [containerRef, focusableSelector],
  );

  return { onKeyDown };
}
